import { useState } from 'react';
import { Beef, Boxes, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { SelectField } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { form_error_message } from '@/lib/forms';
import {
  use_my_food_products,
  use_create_food_product,
  use_update_food_product,
  use_delete_food_product,
  use_create_food_variant,
  use_update_food_variant,
  use_delete_food_variant,
  use_link_food_catalog_product,
} from '@/features/commerce/hooks';
import {
  FOOD_CATEGORY_OPTIONS,
  type FoodProduct,
  type FoodProductCategory,
} from '@/features/commerce/types';
import {
  EMPTY_VARIANT_DRAFT,
  parse_variant_drafts,
  type VariantDraft,
} from '@/features/product-variant/types';
import { VariantDraftList } from '@/components/variant-draft-list';
import { ProductVariantsEditor } from '@/components/product-variants-editor';
import { ProductPricingPanel } from '@/components/product-pricing-panel';
import { ProductApprovalBadge } from '@/components/product-approval-badge';
import { ImageGalleryEditor } from '@/components/image-gallery-editor';
import { CatalogProductSummary } from '@/components/catalog-product-summary';
import { CatalogProductPicker } from '@/components/catalog-product-picker';
import { use_set_variant_stock } from '@/features/stock/hooks';
import { FeePreviewPanel } from '@/components/fee-preview-panel';
import { use_fee_preview } from '@/features/commission/hooks';
import { SpeciesMultiSelect } from '@/components/species-chips';
import { SPECIES_OPTIONS, type PetSpecies } from '@/features/pet/types';

type FormState = {
  name: string;
  description: string;
  brand: string;
  pack_size: string;
  category: FoodProductCategory;
  is_available: boolean;
  species: PetSpecies[];
  variants: VariantDraft[];
};

const EMPTY: FormState = {
  name: '',
  description: '',
  brand: '',
  pack_size: '',
  category: 'DRY_FOOD',
  is_available: true,
  species: [],
  variants: [{ ...EMPTY_VARIANT_DRAFT }],
};

/** Lowest variant price, formatted — the "from" price shown on a product row. */
function from_price(product: FoodProduct): string | null {
  if (product.variants.length === 0) return null;
  const min = Math.min(...product.variants.map((v) => Number(v.price_inr)));
  return Number.isFinite(min) ? String(min) : null;
}

function ProductRow({ product, on_edit }: { product: FoodProduct; on_edit: (p: FoodProduct) => void }) {
  const update_m = use_update_food_product();
  const del_m = use_delete_food_product();
  const create_variant_m = use_create_food_variant();
  const update_variant_m = use_update_food_variant();
  const delete_variant_m = use_delete_food_variant();
  const set_stock_m = use_set_variant_stock(['food-products']);
  const price = from_price(product);

  async function remove() {
    if (!window.confirm(`Delete “${product.name}”? This cannot be undone.`)) return;
    await del_m.mutateAsync(product.id);
  }

  return (
    <li className="flex flex-col gap-2.5 p-3" data-testid={`food-product-${product.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className={cn('flex-1 font-medium', !product.is_available && 'text-muted-foreground line-through')}>
          {product.name}
        </span>
        {product.brand && <span className="text-xs text-muted-foreground">{product.brand}</span>}
        {/* Which pets it is for (client change, 9 Oct 2026); nothing shown = all pets. */}
        {(product.species?.length ?? 0) > 0 && (
          <span className="text-xs text-muted-foreground" data-testid={`food-product-species-${product.id}`}>
            For {product.species!.map((s) => SPECIES_OPTIONS.find((o) => o.value === s)?.label ?? s).join(', ')}
          </span>
        )}
        {price != null && <span className="font-heading font-bold tabular-nums">from ₹{price}</span>}
        <button
          type="button"
          onClick={() => update_m.mutate({ id: product.id, input: { is_available: !product.is_available } })}
          data-testid={`toggle-food-available-${product.id}`}
          className={cn(
            'rounded-full px-2.5 py-1 text-xs font-semibold transition-colors',
            product.is_available ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground',
          )}
        >
          {product.is_available ? 'Available' : 'Hidden'}
        </button>
        <Button size="sm" variant="ghost" onClick={() => on_edit(product)} aria-label="Edit product" data-testid={`edit-food-product-${product.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={remove} aria-label="Delete product" data-testid={`delete-food-product-${product.id}`}>
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
      {/*
        * Where this listing stands with the reviewer. A vendor whose product is
        * invisible needs to know that before they wonder why nothing sells.
        */}
      <ProductApprovalBadge product={product} />

      {/*
        * The platform fee is added on top of the vendor's price, so the number they
        * typed is not the number a shopper sees. All three figures together, or a
        * commission comes as a surprise later.
        */}
      <ProductPricingPanel
        pricing={(product.pricing ?? []).map((p) => ({
          ...p,
          label: product.variants.find((v) => v.id === p.variant_id)?.label,
        }))}
        className="mt-3"
      />

      <ProductVariantsEditor
        variants={product.variants}
        onAdd={(input) => create_variant_m.mutateAsync({ product_id: product.id, input })}
        onUpdate={(id, input) => update_variant_m.mutateAsync({ id, input })}
        onDelete={(id) => delete_variant_m.mutateAsync(id)}
        onSetStock={(id, stock_qty) =>
          set_stock_m.mutateAsync({ vertical: 'FOOD', variant_id: id, stock_qty })
        }
      />
      {/*
        * A linked listing already has photos and details — they belong to the master, and
        * nothing here said so. A vendor saw an empty upload box and concluded their product
        * had none, which is the bug this block answers.
        */}
      {product.is_from_catalog && <CatalogProductSummary product={product} />}

      <ImageGalleryEditor
        owner_type="FOOD_PRODUCT"
        owner_id={product.id}
        label={product.is_from_catalog ? 'Your own photos (optional)' : 'Product photos'}
      />
    </li>
  );
}

function CategoryCard({
  category,
  products,
  on_edit,
}: {
  category: FoodProductCategory;
  products: FoodProduct[];
  on_edit: (p: FoodProduct) => void;
}) {
  const label = FOOD_CATEGORY_OPTIONS.find((o) => o.value === category)?.label ?? category;
  return (
    <Card data-testid={`food-category-${category}`}>
      <div className="border-b border-border p-4">
        <h3 className="font-heading text-lg font-bold">{label}</h3>
      </div>
      <ul className="divide-y-2 divide-brand-outline-variant/10">
        {products.map((p) => (
          <ProductRow key={p.id} product={p} on_edit={on_edit} />
        ))}
      </ul>
    </Card>
  );
}

export function VendorFoodProductsPage() {
  const products_q = use_my_food_products();
  const create_m = use_create_food_product();
  const update_m = use_update_food_product();

  const [editing, set_editing] = useState<null | { id?: string }>(null);
  const [form, set_form] = useState<FormState>(EMPTY);
  const [error, set_error] = useState<string | null>(null);
  /** The catalogue picker is a second way in, not a mode of the manual form. */
  /**
   * Set while editing a catalogue-linked product, to the master's name.
   *
   * Its name, brand, category, pack size and description belong to the master — the
   * API refuses those edits — so the form must not offer them. An editable box the
   * server rejects is how a vendor concludes the console is broken.
   */
  const [linked, set_linked] = useState<string | null>(null);
  /*
   * The fee that will apply to what is being typed. Keyed on the form's category, so
   * switching category updates the preview and typing a price costs no requests.
   */
  const fee_q = use_fee_preview('FOOD', form.category);
  const [picking, set_picking] = useState(false);
  const link_m = use_link_food_catalog_product();

  function open_new() {
    set_linked(null);
    set_error(null);
    set_form(EMPTY);
    set_editing({});
  }

  function open_edit(p: FoodProduct) {
    set_error(null);
    set_form({
      name: p.name,
      description: p.description ?? '',
      brand: p.brand ?? '',
      pack_size: p.pack_size ?? '',
      category: p.category,
      is_available: p.is_available,
      species: p.species ?? [],
      variants: [{ ...EMPTY_VARIANT_DRAFT }], // unused in edit mode — variants managed per-row
    });
    set_linked(p.is_from_catalog ? p.name : null);
    set_editing({ id: p.id });
  }

  function close_form() {
    set_linked(null);
    set_editing(null);
    set_error(null);
  }

  async function submit() {
    set_error(null);
    if (!linked && !form.name.trim()) return set_error('Name is required.');

    const product_fields = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      brand: form.brand.trim() || undefined,
      pack_size: form.pack_size.trim() || undefined,
      category: form.category,
      is_available: form.is_available,
      species: form.species,
    };

    try {
      if (editing?.id) {
        // Edit mode changes product fields only; variants are managed inline on the product row.
        // A catalogue-linked listing may only change its own half: the API refuses
        // name/brand/category/pack size/description/Rx (CATALOG_FIELD_NOT_EDITABLE), so
        // sending the whole form made every save on a linked product fail.
        await update_m.mutateAsync({
          id: editing.id,
          input: linked ? { is_available: form.is_available, species: form.species } : product_fields,
        });
      } else {
        const parsed = parse_variant_drafts(form.variants);
        if ('error' in parsed) return set_error(parsed.error);
        await create_m.mutateAsync({ ...product_fields, variants: parsed.variants });
      }
      close_form();
    } catch (err) {
      set_error(form_error_message(err, 'Could not save the product.'));
    }
  }

  const saving = create_m.isPending || update_m.isPending;
  const products = products_q.data ?? [];
  const grouped = FOOD_CATEGORY_OPTIONS.map((o) => ({
    category: o.value,
    products: products.filter((p) => p.category === o.value),
  })).filter((g) => g.products.length > 0);

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Products"
        description="Your pet-food catalog — dry & wet food, treats, supplements, and toys."
        action={
          !editing &&
          !picking && (
            <div className="flex gap-2">
              {/*
               * Catalogue first: for a common product it is one search and a price,
               * and the listing stays linked so our corrections reach it.
               */}
              <Button
                onClick={() => set_picking(true)}
                variant="outline"
                size="sm"
                data-testid="add-from-catalogue"
              >
                <Boxes className="h-4 w-4" /> Add from catalogue
              </Button>
              <Button onClick={open_new} variant="gradient" size="sm" data-testid="add-food-product">
                <Plus className="h-4 w-4" /> Add product
              </Button>
            </div>
          )
        }
      />

      {picking && (
        <CatalogProductPicker
          vertical="FOOD"
          linking={link_m.isPending}
          on_close={() => set_picking(false)}
          on_link={async (catalog_product_id, variants) => {
            await link_m.mutateAsync({ catalog_product_id, variants });
          }}
        />
      )}

      {/* Create / edit form */}
      {editing && (
        <Card data-testid="food-product-form">
          <div className="flex items-center justify-between border-b-2 border-brand-outline-variant/15 px-5 py-3.5">
            <h2 className="font-heading text-base font-bold">{editing.id ? 'Edit product' : 'New product'}</h2>
            <Button variant="ghost" size="icon" onClick={close_form} aria-label="Close" data-testid="food-product-cancel">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardContent className="flex flex-col gap-4 p-5">
            {linked && (
              /*
               * The platform keeps these current for every vendor selling this
               * product, which is what makes one product one product rather than
               * twenty spellings. Saying so is kinder than a greyed-out box.
               */
              <div
                className="rounded-input border-2 border-input bg-secondary/40 px-4 py-3"
                data-testid="linked-identity-note"
              >
                <p className="text-sm font-semibold">{linked}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Name, brand, category, pack size and the photo come from the Pet Market catalogue and
                  are kept up to date for you. Your price, sizes, availability and your own photos are
                  yours to set.
                </p>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              {!linked && (
                <>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="food-product-name">Name</Label>
                  <Input
                    id="food-product-name"
                    data-testid="food-product-name"
                    value={form.name}
                    onChange={(e) => set_form((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Chicken & Rice Dry Food"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="food-product-category">Category</Label>
                  <SelectField
                    id="food-product-category"
                    data-testid="food-product-category"
                    value={form.category}
                    onValueChange={(v) => set_form((f) => ({ ...f, category: v as FoodProductCategory }))}
                    options={FOOD_CATEGORY_OPTIONS}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="food-product-pack-size" className="flex items-center gap-1">
                    Pack size <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    id="food-product-pack-size"
                    data-testid="food-product-pack-size"
                    value={form.pack_size}
                    onChange={(e) => set_form((f) => ({ ...f, pack_size: e.target.value }))}
                    placeholder="3 kg"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="food-product-brand" className="flex items-center gap-1">
                    Brand <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    id="food-product-brand"
                    data-testid="food-product-brand"
                    value={form.brand}
                    onChange={(e) => set_form((f) => ({ ...f, brand: e.target.value }))}
                    placeholder="Royal Canin"
                  />
                </div>
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <Label htmlFor="food-product-desc" className="flex items-center gap-1">
                    Description <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                  </Label>
                  <textarea
                    id="food-product-desc"
                    data-testid="food-product-desc"
                    value={form.description}
                    onChange={(e) => set_form((f) => ({ ...f, description: e.target.value }))}
                    rows={2}
                    className="rounded-input border-2 border-input bg-card px-4 py-3 text-base text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15"
                    placeholder="What's included…"
                  />
                </div>
                </>
              )}
              {/* Client change, 9 Oct 2026: which pets this is for, so a Bird filter hides Dog food.
                  Editable on catalogue-linked listings too, and never sent for review. */}
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label>Suitable for</Label>
                <SpeciesMultiSelect
                  testid="food-product-species"
                  value={form.species}
                  onChange={(species) => set_form((f) => ({ ...f, species }))}
                />
              </div>
              <label
                className="flex cursor-pointer items-center justify-between gap-3 rounded-input border-2 border-input bg-secondary/40 px-4 py-2.5 sm:col-span-2"
                data-testid="food-product-available-label"
              >
                <span className="flex flex-col">
                  <span className="text-sm font-semibold">Available</span>
                  <span className="text-xs text-muted-foreground">Visible to customers and orderable</span>
                </span>
                <input
                  type="checkbox"
                  data-testid="food-product-available"
                  checked={form.is_available}
                  onChange={(e) => set_form((f) => ({ ...f, is_available: e.target.checked }))}
                  className="h-5 w-5 shrink-0 accent-primary"
                />
              </label>
            </div>

            {editing.id ? (
              <p className="text-xs text-muted-foreground">Manage this product’s variants from its row in the list below.</p>
            ) : (
              <VariantDraftList value={form.variants} onChange={(variants) => set_form((f) => ({ ...f, variants }))} />
            )}

            {!editing.id && (
              <FeePreviewPanel fee={fee_q.data} variants={form.variants} />
            )}

            {error && (
              <p className="text-sm text-destructive" data-testid="food-product-form-error" role="alert">
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <Button onClick={submit} variant="gradient" disabled={saving} data-testid="food-product-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editing.id ? 'Save changes' : 'Create product'}
              </Button>
              <Button onClick={close_form} variant="ghost">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List, grouped by category */}
      {products_q.isLoading ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : products_q.error ? (
        <ErrorState
          data-testid="food-products-error"
          title="Couldn't load your products"
          description={form_error_message(products_q.error, 'Please try again.')}
        />
      ) : grouped.length === 0 ? (
        <EmptyState
          data-testid="food-products-empty"
          icon={Beef}
          title="No products yet"
          description="Add your first product above to get started."
        />
      ) : (
        <div className="flex flex-col gap-4" data-testid="food-products-categories">
          {grouped.map((g) => (
            <CategoryCard key={g.category} category={g.category} products={g.products} on_edit={open_edit} />
          ))}
        </div>
      )}
    </div>
  );
}
